import { ADToBS } from "bikram-sambat-js";
import { format, isValid, parseISO } from "date-fns";
import { useCallback } from "react";
import {
  DateCalendarMode,
  useDateCalendarMode,
} from "../../hooks/useDateCalendarMode";

/** Bikram Sambat month names, Baisakh first. */
export const BS_MONTH_NAMES = [
  "Baisakh",
  "Jestha",
  "Asar",
  "Shrawan",
  "Bhadra",
  "Asoj",
  "Kartik",
  "Mangsir",
  "Poush",
  "Magh",
  "Falgun",
  "Chaitra",
] as const;

export interface CalendarDateOptions {
  /** Add the time of day ("3:41 PM"). */
  time?: boolean;
}

function toDate(value: Date | string | null | undefined): Date | null {
  if (!value) return null;
  const date = typeof value === "string" ? parseISO(value) : value;
  return isValid(date) ? date : null;
}

/**
 * A date written for people, in the AD/BS preference: "16 Oct 2026" or
 * "30 Asoj 2083", with the time when asked. The BS date comes from
 * `bikram-sambat-js` (the library the BS date picker uses), never from
 * arithmetic here. Unreadable input gives "".
 */
export function formatCalendarDate(
  value: Date | string | null | undefined,
  mode: DateCalendarMode,
  { time = false }: CalendarDateOptions = {},
): string {
  const date = toDate(value);
  if (!date) return "";
  const clock = time ? `, ${format(date, "h:mm a")}` : "";
  if (mode === "BS") {
    try {
      const [year, month, day] = ADToBS(format(date, "yyyy-MM-dd"))
        .split("-")
        .map(Number);
      const name = BS_MONTH_NAMES[month - 1];
      if (name) return `${day} ${name} ${year}${clock}`;
    } catch {
      // Outside the library's range: fall back to AD below.
    }
  }
  return `${format(date, "d MMM yyyy")}${clock}`;
}

/** `formatCalendarDate` bound to the page's AD/BS switch. */
export function useCalendarDateFormat(): (
  value: Date | string | null | undefined,
  options?: CalendarDateOptions,
) => string {
  const [mode] = useDateCalendarMode();
  return useCallback(
    (value, options) => formatCalendarDate(value, mode, options),
    [mode],
  );
}
